import { useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPublicEventosInfo } from "@/features/publicEventos/publicEventosThunks";
import { setLoadingPublicEventos } from "@/features/publicEventos/publicEventosSlice";
import {
  getExperienciasFijo,
  getExperienciasFinDeSemana,
  getExperienciasTop,
} from "@/features/experiencias/experienciasThunks";

export default function CategoryBar({ categories }) {
  const dispatch = useDispatch();
  const barRef = useRef(null);
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const isLoading = useSelector((state) => state.publicEventos.isLoadingPublicEventos);

  // Use IntersectionObserver to detect when the bar becomes sticky
  useEffect(() => {
    const sentinelC = sentinelRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Si el sentinel no está visible, significa que el bar está pegado (sticky)
        setIsSticky(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
      }
    );
    if (sentinelC) {
      observer.observe(sentinelC);
    }

    return () => {
      if (sentinelC) {
        observer.unobserve(sentinelC);
      }
    };
  }, []);

  const handleCategoryClick = (category, idx) => {
    setActiveIdx(idx);
    
    // Mostrar loading inmediatamente
    dispatch(setLoadingPublicEventos(true));
    
    // Si es "Todos los Tickets", no enviar filtro de categoría
    const now = new Date().toISOString();
    const farPast = '1970-01-01T00:00:00.000Z';
    
    const filters = {
      page: 1,
      limit: 10,
      fechaHoraInicioDesde: farPast,  // Allow started events
      fechaHoraFinDesde: now          // Only show events that haven't ended
    };
    
    // Solo agregar categoría si NO es "Todos los Tickets"
    if (category !== "Todos los Tickets") {
      filters.categoriaEvento = category;
    }
    
    // Dispatch thunk con filtros
    dispatch(getPublicEventosInfo(filters));

    // También filtrar experiencias por categoría
    const experienciaFilters = {
      page: 1,
      limit: 10,
      ...(category !== "Todos los Tickets" ? { categoria: category } : {}),
    };
    dispatch(getExperienciasFijo(experienciaFilters));
    dispatch(getExperienciasFinDeSemana(experienciaFilters));
    dispatch(getExperienciasTop(experienciaFilters));
  };

  return (
    <>
      {/* Este div es invisible, solo sirve para saber cuándo el sticky entra en acción */}
      <div ref={sentinelRef} className="h-0" />
      <div
        ref={barRef}
        className={`w-full mb-3 sticky top-0 transition-colors duration-300 ${
          isSticky ? "bg-aw-bluedark/90 z-30" : "bg-transparent z-10"
        }`}
      >
        <div className="flex flex-row gap-3 overflow-x-auto py-2 scrollbar-box">
          {categories.map((cat, idx) => (
            <button
              className={`rounded-lg px-3 py-1 text-sm text-aw-bluedark font-medium whitespace-nowrap transition-colors border-none select-none ${
                activeIdx === idx
                  ? "bg-aw-green"
                  : "bg-gray-200 hover:bg-gray-300"
              } ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              key={idx}
              onClick={() => handleCategoryClick(cat, idx)}
              disabled={isLoading}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
